/**
 * ISTO Variant Sort
 *
 * O ERP fornece os valores de tamanho (opção "Size") sem ordem garantida.
 * Este script reordena os pickers de variantes no browser, com base num
 * dicionário definido no metafield de loja `custom.size_dictionary`.
 *
 * Estratégia (performance "in mind"):
 * - Progressive enhancement: o HTML já vem funcional do servidor, isto
 *   apenas reordena os nós existentes (não os recria), preservando
 *   listeners e estado (checked, disabled, etc.).
 * - Zero custo quando a ordem já está correta ou quando os valores não
 *   correspondem a nenhum padrão conhecido (ex: cor, material) - nesses
 *   casos o array/DOM não é tocado.
 * - O dicionário é lido uma única vez de uma <script type="application/json">
 *   já presente no HTML (sem pedidos de rede extra).
 */
(function () {
  let cachedDictionary = null;

  function getSizeDictionary() {
    if (cachedDictionary) return cachedDictionary;

    const node = document.getElementById('isto-size-dictionary');
    try {
      cachedDictionary = (node && JSON.parse(node.textContent)) || {};
    } catch (error) {
      cachedDictionary = {};
    }
    return cachedDictionary;
  }

  /**
   * Ordena um array de strings (valores de tamanho).
   * Devolve o próprio array (mesma referência) quando nada muda ou quando
   * o conjunto de valores não corresponde a nenhuma regra conhecida.
   */
  function sortSizeValues(values) {
    if (!Array.isArray(values) || values.length < 2) return values;

    const dictionary = getSizeDictionary();

    function splitByMatcher(sourceValues, matcher) {
      const matched = [];
      const unmatched = [];

      sourceValues.forEach((value) => {
        const match = matcher(value);
        if (match) {
          matched.push({ value, match });
        } else {
          unmatched.push(value);
        }
      });

      return { matched, unmatched };
    }

    function combineSortedValues(sortedMatched, unmatched) {
      return sortedMatched.concat(unmatched);
    }

    const waistLengthConfig = dictionary.waist_length;
    if (waistLengthConfig && waistLengthConfig.pattern) {
      const pattern = new RegExp(waistLengthConfig.pattern);
      const { matched, unmatched } = splitByMatcher(values, (value) => String(value).trim().match(pattern));

      if (matched.length) {
        const sortedMatched = matched
          .slice()
          .sort(
            (a, b) => Number(a.match[1]) - Number(b.match[1]) || Number(a.match[2]) - Number(b.match[2])
          )
          .map((item) => item.value);
        return combineSortedValues(sortedMatched, unmatched);
      }
    }

    const numericSplit = splitByMatcher(values, (value) => (/^\d+$/.test(String(value).trim()) ? String(value).trim() : null));
    if (numericSplit.matched.length) {
      const sortedMatched = numericSplit.matched
        .slice()
        .sort((a, b) => Number(a.value) - Number(b.value))
        .map((item) => item.value);
      return combineSortedValues(sortedMatched, numericSplit.unmatched);
    }

    const letterOrder = Array.isArray(dictionary.letter_sizes) ? dictionary.letter_sizes : [];
    if (letterOrder.length) {
      const { matched, unmatched } = splitByMatcher(values, (value) => {
        const normalized = String(value).trim().toUpperCase();
        return letterOrder.includes(normalized) ? normalized : null;
      });

      if (matched.length) {
        const sortedMatched = matched
          .slice()
          .sort((a, b) => letterOrder.indexOf(a.match) - letterOrder.indexOf(b.match))
          .map((item) => item.value);
        return combineSortedValues(sortedMatched, unmatched);
      }
    }

    return values;
  }

  function sameOrder(a, b) {
    return a.length === b.length && a.every((value, index) => value === b[index]);
  }

  /**
   * Ordena qualquer lista de itens com base num texto extraído de cada um.
   * Devolve a própria lista (mesma referência) quando nada muda.
   */
  function sortByText(items, getText) {
    if (!Array.isArray(items) || items.length < 2) return items;

    const texts = items.map(getText);
    const sortedTexts = sortSizeValues(texts);
    if (sortedTexts === texts) return items;

    const buckets = new Map();
    items.forEach((item, index) => {
      const key = texts[index];
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(item);
    });

    return sortedTexts.map((key) => buckets.get(key).shift());
  }

  function sortSelectOptions(select) {
    const options = Array.from(select.options);
    const sorted = sortByText(options, (option) => option.value);
    if (sorted === options) return;
    sorted.forEach((option) => select.appendChild(option));
  }

  function sortFieldsetInputs(fieldset) {
    const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"], input[type="checkbox"]'));
    const sorted = sortByText(inputs, (input) => input.value);
    if (sorted === inputs) return;

    sorted.forEach((input) => {
      fieldset.appendChild(input);
      const label = fieldset.querySelector(`label[for="${input.id}"]`);
      if (label) fieldset.appendChild(label);
    });
  }

  function sortQuickAddButtons(form) {
    const buttons = Array.from(form.querySelectorAll('.variant-button'));
    const sorted = sortByText(buttons, (button) => button.querySelector('span')?.textContent.trim() || '');
    if (sorted === buttons) return;
    sorted.forEach((button) => form.appendChild(button));
  }

  function sortQuickAddContainers(scope) {
    scope.querySelectorAll('.quick-add__container').forEach((container) => {
      const buttons = container.querySelectorAll('.variant-button');
      if (buttons.length < 2) return;
      sortQuickAddButtons(buttons[0].parentElement);
    });
  }

  function sortCollectionProductGrids(scope) {
    const grids = [];

    if (scope?.matches?.('.collection-page-grid')) {
      grids.push(scope);
    }

    scope.querySelectorAll?.('.collection-page-grid').forEach((grid) => {
      grids.push(grid);
    });

    grids.forEach((grid) => {
      const productItems = Array.from(grid.children).filter((child) => child.dataset.productAvailable !== undefined);
      if (productItems.length < 2) return;

      const sortedItems = productItems
        .map((item, index) => ({ item, index }))
        .sort((left, right) => {
          const leftAvailable = left.item.dataset.productAvailable !== 'false';
          const rightAvailable = right.item.dataset.productAvailable !== 'false';

          if (leftAvailable !== rightAvailable) {
            return leftAvailable ? -1 : 1;
          }

          return left.index - right.index;
        })
        .map((entry) => entry.item);

      sortedItems.forEach((item) => grid.appendChild(item));
    });
  }

  /**
   * Ponto de entrada: reordena todos os pickers de tamanho dentro de `root`.
   * `root` pode ser o document (carregamento inicial) ou um container
   * inserido dinamicamente (ex: conteúdo do quick-add-modal via fetch).
   */
  function sortAll(root) {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;

    scope
      .querySelectorAll('fieldset.product-form__input--swatch, fieldset.product-form__input--pill')
      .forEach(sortFieldsetInputs);

    scope.querySelectorAll('.product-form__input--dropdown select.select__select').forEach(sortSelectOptions);

    sortQuickAddContainers(scope);
    sortCollectionProductGrids(scope);
  }

  /**
   * O <product-info> re-renderiza o <variant-selects> com HTML do servidor
   * (ordem do ERP) em cada mudança de variante / swap de produto. Sem este
   * hook, a ordenação feita no load reverteria ao primeiro clique.
   *
   * O product-info expõe `addPreProcessCallback`, executado sobre o novo
   * fragmento HTML ANTES de ser inserido no DOM - ou seja, reordenamos a
   * fonte antes do swap, sem flash e sem reflow adicional.
   */
  function hookProductInfo(productInfo) {
    if (!productInfo || typeof productInfo.addPreProcessCallback !== 'function') return;
    if (productInfo.dataset.istoSortHooked === 'true') return;

    productInfo.dataset.istoSortHooked = 'true';
    productInfo.addPreProcessCallback((newContent) => sortAll(newContent));
  }

  window.ISTOVariantSort = { sortSizeValues, sortByText, sortAll };

  document.addEventListener('product-info:loaded', (event) => hookProductInfo(event.target));

  document.addEventListener('DOMContentLoaded', () => {
    sortAll(document);
    document.querySelectorAll('product-info').forEach(hookProductInfo);
  });
})();
